/**
 * DemogChartTab — the chart's **Demog** inner tab (PROD-1988).
 *
 * Flutter source of truth: `demog_tab.dart` (patient_chart/.../tab_widgets).
 * Ported to the letter per Andrew (2026-08-19, "do it as flutter exactly"):
 *
 *   body() :299        → the summarised row, then a Row of [ left 239px | right ].
 *                        (That summarised row — MRN · Language · DOB · Last updated ·
 *                        Status — is already rendered above EVERY chart tab by
 *                        ChartTabBody as PatientChartSummaryHeader, which IS the port
 *                        of Flutter's PatientSummarisedRowWidget, so it is not
 *                        repeated here.)
 *   leftPanel() :822   → FormView header "DEMOGRAPHICS", 239px, all READ-ONLY:
 *                        photo · NAME · DATE OF BIRTH (date + age) · GENDER ·
 *                        ID TYPE · ID NUMBER · ID EXPIRATION DATE ·
 *                        ID FRONT SIDE · ID BACK SIDE (Submitted / Not Submitted).
 *   rightPanel() :355  → Save (top-right) + a HEADERLESS FormView of exactly three
 *                        editable fields (MARITAL STATUS · PREFERRED LANGUAGE ·
 *                        INTERPRETER REQUIRED), then OccupationForm (only when
 *                        `isAdult`), EducationForm, CONTACT DETAILS (gated on the
 *                        `Patient_Contacts` view permission), RESIDENTIAL ADDRESS,
 *                        EmergencyContactsWidget, HealthInsuranceWidget.
 *
 * ── ONE SAVE (the PROD-1988 review fix) ─────────────────────────────────────────
 * Andrew, 2026-08-19: "there is issue with the saves, not all sections has save."
 * Correct, and the browser confirmed it: the first cut had TWO unlabelled "Save"
 * buttons floating above cards, so which card either one committed was unguessable.
 *
 * Flutter has exactly ONE. Every editable field in the right panel — the three
 * preferences (`:400`,`:462`,`:548`), OccupationForm (`occupation_form.dart:52,78,109`),
 * EducationForm, the contact edit widget and the residential edit widget — fires the
 * SAME `EnablePatientSaveEvent(patient:)`, which enables the single `saveButton()`
 * (`:960`); pressing it issues ONE `UpdatePatientEvent` for the whole patient object.
 * The contact/residential widgets' own `onSave` callbacks only leave edit mode
 * (`demog_tab.dart` passes `PatientAddressEditEvent(isEdit: false)`) — they do not
 * persist. Only EmergencyContactsWidget and HealthInsuranceWidget own a real Save,
 * because they write through their own endpoints.
 *
 * So: both react-hook-form instances live HERE, one Save button reads both, and the
 * two projections compose into a single body for a single PUT —
 * `mergePatientWithContactValues(toPatientBody(personal), contact)` — which is exactly
 * Flutter's one-patient write. Flutter's save also gates on
 * `Manage_Patients_Demographics` edit and otherwise refuses ("You are not authorized
 * to update data"); that gate is ported as a disabled button plus the reason, so the
 * refusal is visible before the click rather than after it.
 *
 * Composition, not reconstruction: every editor is a surface this repo already ships
 * (`PatientPreferencesFields`, `OccupationSection`, `EducationSection`,
 * `ContactDetailsForm`, `AddressList`, `EmergencyContactsSection`,
 * `HealthInsuranceSection`). What this file owns is Flutter's LAYOUT and its Save —
 * deliberately NOT `usePatientEditShell()`, which belongs to the patient-edit shell's
 * top bar; Flutter's Demog tab carries its own button.
 */

import { useEffect, useMemo } from 'react';
import { useForm, FormProvider, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { useAuthStore } from '@/auth/auth-store';
import { usePermission } from '@/auth/permission-gate';
import { SessionOrgPending } from '@/patients/tabs/session-org-pending';
import { useGenders, useIdTypes } from '@/shared/services/lookups';
import { useUnsavedChangesSource } from '@/shared/ui/components/unsaved-changes';
import { SectionCard } from '@/patients/components/personal-details/section-card';
import { PatientPreferencesFields } from '@/patients/components/personal-details/patient-preferences-fields';
import { OccupationSection } from '@/patients/components/personal-details/occupation-section';
import { EducationSection } from '@/patients/components/personal-details/education-section';
import { ContactDetailsForm } from '@/patients/components/contact-details/contact-details-form';
import { AddressList } from '@/patients/components/contact-details/address-list';
import { EmergencyContactsSection } from '@/patients/components/emergency-contacts/emergency-contacts-section';
import { HealthInsuranceSection } from '@/patients/components/health-insurance/health-insurance-section';
import {
  contactDetailsSchema,
  type ContactDetailsFormValues,
} from '@/patients/components/contact-details/contact-details-schema';
import type { PersonalDetailsFormValues } from '@/patients/components/personal-details/personal-details-schema';
import { patientToFormValues, toPatientBody } from '@/patients/tabs/personal-details-tab';
import {
  patientToContactValues,
  mergePatientWithContactValues,
} from '@/patients/tabs/contact-details-tab';
import { useUpdatePatient } from '@/patients/services/use-update-patient';
import type { Patient } from '@/types/patient-api';
import { EmptyState } from '@scripsteam/scrips-signal-ds';

/** True when react-hook-form's `dirtyFields` flags at least one field (it nests objects and arrays of booleans). */
function hasEditedField(node: unknown): boolean {
  if (node === true) return true;
  if (Array.isArray(node)) return node.some(hasEditedField);
  if (node && typeof node === 'object') return Object.values(node).some(hasEditedField);
  return false;
}

export interface DemogChartTabProps {
  patient: Patient | null;
}

/**
 * PROD-2959: organization and practice are the patient's own, else the signed-in user's — never a
 * hard-coded DEV id. Until both are known the tab renders `SessionOrgPending`, not the forms.
 */
export function DemogChartTab(props: DemogChartTabProps) {
  const auth = useAuthStore((s) => s.user);
  const orgId = props.patient?.organizationId ?? auth?.organizationId;
  const practiceId = props.patient?.practiceId ?? auth?.practiceId;
  if (!orgId || !practiceId) return <SessionOrgPending userLoaded={!!auth} />;
  return <DemogChartTabBody {...props} orgId={orgId} practiceId={practiceId} />;
}

function DemogChartTabBody({ patient, orgId, practiceId }: DemogChartTabProps & { orgId: string; practiceId: string }) {

  // Flutter gates the CONTACT DETAILS card on `validatePermission([Patient_Contacts],
  // view: true)` (`demog_tab.dart:583`), and its Save on `Manage_Patients_Demographics`
  // edit (`:986`).
  const canViewContacts = usePermission(['Patient_Contacts'], { view: true });
  const canEditContacts = usePermission(['Patient_Contacts'], { edit: true });
  const canEditDemographics = usePermission(['Manage_Patients_Demographics'], {
    edit: true,
  });
  const merged = patient?.isMerged === true;

  // BOTH forms live here so ONE Save (the page's Unsaved changes bar) can read them (see the header note). Their
  // schemas differ, so they stay two RHF instances feeding two FormProviders — the
  // shipped section components each expect their own context — but they commit
  // together, in one PUT, as one patient.
  const personalForm = useForm<PersonalDetailsFormValues>({
    // No zod resolver on purpose: this surface edits three optional preference fields
    // plus occupation/education, and the full personal-details schema would block Save
    // on required fields (name, DOB) it does not even render. Flutter's gate is
    // "something changed" — EnablePatientSaveEvent.
    defaultValues: patient
      ? patientToFormValues(patient, orgId, practiceId)
      : undefined,
  });
  const contactInitial = useMemo<ContactDetailsFormValues>(
    () => patientToContactValues(patient),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [patient?.patientId],
  );
  const contactForm = useForm<ContactDetailsFormValues>({
    defaultValues: contactInitial,
    resolver: zodResolver(contactDetailsSchema),
    mode: 'onChange',
  });

  // Re-seed both when the patient settles / changes (same pattern as the patient-edit tabs).
  useEffect(() => {
    if (!patient?.patientId) return;
    personalForm.reset(patientToFormValues(patient, orgId, practiceId));
    contactForm.reset(patientToContactValues(patient));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.patientId]);

  const updateMutation = useUpdatePatient();
  // `isDirty` alone stays true after a reset here (the education section writes `{}` where the saved value is
  // null), so a form counts as changed only when a field is flagged as edited.
  const personalDirty = personalForm.formState.isDirty && hasEditedField(personalForm.formState.dirtyFields);
  const contactDirty = contactForm.formState.isDirty && hasEditedField(contactForm.formState.dirtyFields);
  const isDirty = personalDirty || contactDirty;

  // Flutter's press handler: permission check, then ONE UpdatePatientEvent for the
  // whole patient. Here: compose both projections into one body, one PUT.
  const onSave = async (): Promise<boolean | string> => {
    const patientId = patient?.patientId;
    if (!patientId) return false;
    // Why a save cannot go through, said in the bar instead of beside a disabled button.
    if (saveBlockedReason) return saveBlockedReason;
    // Show the contact form's own errors where they are, and keep the bar.
    if (contactDirty && !(await contactForm.trigger())) return 'Fix the highlighted contact fields to save.';
    const withPersonal = toPatientBody(personalForm.getValues(), patient);
    const body = mergePatientWithContactValues(withPersonal, contactForm.getValues());
    try {
      await updateMutation.mutateAsync({ patientId, patient: body });
      toast.success('Patient updated');
      personalForm.reset(personalForm.getValues());
      contactForm.reset(contactForm.getValues());
      return true;
    } catch {
      // The QueryClient's global onError surfaces server errors.
      return false;
    }
  };
  // Cancel = back to the last saved values (reset() with no argument returns to the defaults, which a save moves).
  const onCancel = () => {
    personalForm.reset();
    contactForm.reset();
  };

  const saveBlockedReason = merged
    ? 'This patient is merged and cannot be edited.'
    : !canEditDemographics
      ? // Flutter's own refusal copy (`demog_tab.dart:995`), shown up front instead of
        // on the click.
        'You are not authorized to update data.'
      : null;

  // Inside a page that owns the "Unsaved changes" bar, that bar carries Save and Cancel; elsewhere this column keeps
  // main's own Save (below), as the emergency contacts and insurance sections do.
  const hosted = useUnsavedChangesSource({ dirty: isDirty, saving: updateMutation.isPending, save: onSave, cancel: onCancel });

  if (!patient?.patientId) {
    return (
      <div className="p-6">
        <EmptyState icon="info" title="Save the patient first to view their demographics." />
      </div>
    );
  }

  const readOnly = merged || !canEditDemographics;
  const isAdult = patient.isAdult ?? true;

  return (
    // Flutter: SingleChildScrollView > Padding(all 24) > Column. The Row becomes a
    // wrapping flex so Flutter's compact branch (its `Wrap` under 700px) falls out of
    // the same rule instead of needing a second layout.
    // Every card here is `flush` (no horizontal margin): the two columns are 24px apart via the
    // row's `gap-6`, which is Flutter's single `SpaceHelper.horizontalSpace24`
    // (`demog_tab.dart:345`). With SectionCard's default `mx-6` the gutter measured 65px against
    // ~23px in the Flutter app (Andrew, 2026-08-20, measured off a Flutter screenshot: "the space
    // between the 2 panels … is wider than in flutter").
    // The design's DemographicsChartTab (export 21-10 chart-demographics.html): the read-only demographics as one
    // Field / Value table, full width; the PA's editable sections (preferences, occupation, education, contacts,
    // emergency contacts, insurance) follow under it with their Save, unchanged.
    <div className="overflow-auto p-6">
      <div className="demog-stack">
        <DemographicsCard patient={patient} />

        {/* The editable sections — Flutter rightPanel(). */}
        <div className="min-w-0">
          {/* THE Save (main) when no page bar hosts it — one button for this whole column, right-aligned above the
              first card as Flutter places it. It commits the preferences, occupation, education, contact details and
              residential address together. Emergency contacts and insurance keep their own, as in Flutter. */}
          {!hosted ? (
            <div className="mb-4 flex items-center justify-end gap-3">
              {saveBlockedReason && isDirty ? (
                <span className="text-xs text-text-muted">{saveBlockedReason}</span>
              ) : null}
              <Button
                type="button"
                size="sm"
                onClick={() => void onSave()}
                disabled={!!saveBlockedReason || !isDirty || updateMutation.isPending}
                title="Saves demographics, occupation, education, contact details and address"
              >
                {updateMutation.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          ) : null}
          <PreferencesSections
            form={personalForm}
            isAdult={isAdult}
            readOnly={readOnly}
          />

          {canViewContacts ? (
            <ContactAndAddressSections
              form={contactForm}
              isAdult={isAdult}
              readOnly={readOnly || !canEditContacts}
            />
          ) : null}

          {/* Own Save (Flutter EmergencyContactsWidget / HealthInsuranceWidget — their
              own endpoints, so their own commit). */}
          <SectionCard header="Emergency contacts">
            <EmergencyContactsSection patientId={patient.patientId} readOnly={readOnly} />
          </SectionCard>

          <SectionCard header="Health insurance">
            <HealthInsuranceSection
              patientId={patient.patientId}
              organizationId={orgId}
              readOnly={readOnly}
            />
          </SectionCard>
        </div>
      </div>
    </div>
  );
}

// ── LEFT: read-only demographics ─────────────────────────────────────────────
/** Flutter `leftPanel()` — every field a `FieldType.Label`, i.e. read-only. The chart
 *  is a clinical read surface; the only editable demographics are the right panel's
 *  three fields, exactly as Flutter has it. */
function DemographicsCard({ patient }: { patient: Patient }) {
  const gendersQ = useGenders();
  const idTypesQ = useIdTypes();

  const genderName =
    patient.genderName?.name ??
    gendersQ.data?.find((g) => g.id === patient.gender)?.name ??
    null;
  const idTypeName =
    idTypesQ.data?.find((t) => t.id === patient.identification?.idType)?.name ?? null;
  const fullName = [patient.firstName, patient.middleName, patient.lastName]
    .filter((p) => (p ?? '').trim().length > 0)
    .join(' ');

  return (
    <section className="demog-read" aria-label="Demographics">
      <div className="demog-read-head">
        {/* Flutter CircleImage(size: 56) — initials when there is no photo, never a broken image frame. */}
        <PatientAvatar url={patient.photoUrl} name={fullName} />
      </div>
      <div className="chart-plain-table">
        <table className="w-full">
          <thead>
            <tr>
              <th>Field</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <ReadField label="Name" value={fullName} />
            <ReadField label="Date of birth" value={formatDobWithAge(patient.birthDate, patient.age)} />
            <ReadField label="Gender" value={genderName} />
            <ReadField label="ID type" value={idTypeName} />
            <ReadField label="ID number" value={patient.identification?.idNumber ?? null} />
            <ReadField label="ID expiration date" value={formatDate(patient.identification?.idExpirationDate)} />
            {/* Flutter renders these as a submitted/not-submitted STATE, not the image. */}
            <ReadField label="ID front side" value={submittedLabel(patient.idFrontImage ?? patient.frontIdCardUrl)} />
            <ReadField label="ID back side" value={submittedLabel(patient.idBackImage ?? patient.backIdCardUrl)} />
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PatientAvatar({ url, name }: { url: string | null; name: string }) {
  const initials =
    name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '—';
  if (url && url.trim().length > 0) {
    return (
      <img
        src={url}
        alt={name || 'Patient photo'}
        className="demog-avatar"
      />
    );
  }
  return (
    <div
      className="demog-avatar"
      aria-label={name || 'Patient'}
    >
      {initials}
    </div>
  );
}

/** One Field / Value row — the design's DemographicsChartTab row (field at body strong, value secondary). */
function ReadField({ label, value }: { label: string; value: string | null }) {
  return (
    <tr>
      <td className="demog-field">{label}</td>
      <td className="demog-value">{value?.trim() || 'N/A'}</td>
    </tr>
  );
}

// ── RIGHT: the three editable fields + occupation + education ────────────────
/** Presentational: the form instance is the parent's, so the single Save can read it. */
function PreferencesSections({
  form,
  isAdult,
  readOnly,
}: {
  form: UseFormReturn<PersonalDetailsFormValues>;
  isAdult: boolean;
  readOnly: boolean;
}) {
  return (
    <FormProvider {...form}>
      {/* HEADERLESS, matching Flutter: the right panel's FormView (`demog_tab.dart:367`)
          takes no `header:` — the "DEMOGRAPHICS" label belongs to the LEFT card only.
          The first cut gave this card that header too, which read as a duplicate. */}
      <fieldset
        disabled={readOnly}
        className="demog-card"
      >
        <div className="flex flex-col gap-6">
          <PatientPreferencesFields />
        </div>
      </fieldset>
      {/* Flutter shows OccupationForm only for an adult patient (`demog_tab.dart:568`). */}
      {isAdult ? (
        <SectionCard header="Occupation">
          <fieldset disabled={readOnly}>
            <OccupationSection />
          </fieldset>
        </SectionCard>
      ) : null}
      <SectionCard header="Education">
        <fieldset disabled={readOnly}>
          <EducationSection />
        </fieldset>
      </SectionCard>
    </FormProvider>
  );
}

// ── RIGHT: contact details + residential address ─────────────────────────────
/** Presentational: same deal — the parent owns the form and the Save. */
function ContactAndAddressSections({
  form,
  isAdult,
  readOnly,
}: {
  form: UseFormReturn<ContactDetailsFormValues>;
  isAdult: boolean;
  readOnly: boolean;
}) {
  return (
    <FormProvider {...form}>
      <SectionCard header="Contact details">
        <ContactDetailsForm isAdult={isAdult} readOnly={readOnly} />
      </SectionCard>
      <SectionCard header="Residential address">
        <AddressList readOnly={readOnly} />
      </SectionCard>
    </FormProvider>
  );
}

// ── Formatting ───────────────────────────────────────────────────────────────
/** Flutter: `"${scDateFormat(birthDate)} (${age})"`. `age` is the server's own display
 *  string, so it is preferred over a locally computed one. */
function formatDobWithAge(
  iso: string | null | undefined,
  age: string | null | undefined,
): string | null {
  const date = formatDate(iso);
  if (!date) return null;
  const a = age?.trim();
  return a ? `${date} (${a})` : date;
}

function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Flutter renders "Submitted" / "Not Submitted" for the ID images. */
function submittedLabel(image: string | null | undefined): string {
  return image && image.trim().length > 0 ? 'Submitted' : 'Not Submitted';
}
